import dayjs, { Dayjs } from 'dayjs';
import { Door, Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useMemo } from 'react';
import { createEventApiFilter } from 'v2/filters/events/createEventApiFilter';
import { createEventFilterChips } from 'v2/filters/events/createEventFilterChips';
import { createEventFilterSections } from 'v2/filters/events/createEventFilterSections';
import { ACCESS_FILTER_ITEMS } from 'v2/filters/events/eventFilterConstants';
import { getEventDeviceOptions, getEventVisitorOptions } from 'v2/filters/events/eventFilterOptions';
import { EventsFilter, EventsFilterState, FilterEventDetail } from 'v2/filters/events/eventFilterTypes';
import { normalizeEventDateRange } from 'v2/filters/events/normalizeEventDateRange';
import { parseDateParam, parseEnumParam, serializeDate, serializeEnum } from 'v2/filters/filterUrlParams';
import { useFilterSearchParams } from 'v2/hooks/useFilterSearchParams/useFilterSearchParams';

interface Params {
  visitors: Visitor[];
  doors: Door[];
}

type EventFilterParam = 'access' | 'detail' | 'user' | 'device' | 'from' | 'to';

const EVENT_FILTER_PARAM_KEYS: EventFilterParam[] = ['access', 'detail', 'user', 'device', 'from', 'to'];

const FILTER_ACCESS_VALUES = ACCESS_FILTER_ITEMS.map(({ value }) => value);
const FILTER_EVENT_DETAIL_VALUES = Object.values(FilterEventDetail);

const readDateRange = (fromParam: string | null, toParam: string | null) =>
  normalizeEventDateRange(parseDateParam(fromParam), parseDateParam(toParam));

// Ignore invalid or future dates emitted by the picker while editing.
const isSelectableDate = (value: Dayjs | null) => value === null || (value.isValid() && !value.isAfter(dayjs()));

export const useEventsSearchAndFilters = ({ visitors, doors }: Params) => {
  const { translation } = useTranslation();

  const { searchParams, updateFilterParams } = useFilterSearchParams<EventFilterParam>(EVENT_FILTER_PARAM_KEYS);

  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');

  const access = parseEnumParam(searchParams.get('access'), FILTER_ACCESS_VALUES);
  const detail = parseEnumParam(searchParams.get('detail'), FILTER_EVENT_DETAIL_VALUES);
  const user = searchParams.get('user') || null;
  const device = searchParams.get('device') || null;
  // Memoized on the raw params so the dates keep their identity, and so does apiFilter, until the URL changes.
  const { dateStart, dateEnd } = useMemo(() => readDateRange(fromParam, toParam), [fromParam, toParam]);

  const filter: EventsFilter = { access, detail, dateStart, dateEnd, user, device };

  const apiFilter = useMemo(
    () => createEventApiFilter({ access, detail, dateStart, dateEnd, user, device }),
    [access, detail, dateStart, dateEnd, user, device]
  );
  const isAnyFilterEnabled = apiFilter !== null;

  const visitorOptions = useMemo(() => getEventVisitorOptions(visitors), [visitors]);
  const deviceOptions = useMemo(() => getEventDeviceOptions(doors), [doors]);

  const clearAllFilters = () =>
    updateFilterParams(() => ({
      access: undefined,
      detail: undefined,
      user: undefined,
      device: undefined,
      from: undefined,
      to: undefined
    }));

  const filters: EventsFilterState = {
    filter,
    setters: {
      setFilterAccess: (value) => updateFilterParams(() => ({ access: serializeEnum(value) })),
      toggleFilterAccess: (value) =>
        updateFilterParams((current) => ({ access: current.get('access') === value ? undefined : value })),
      setFilterDetail: (value) => updateFilterParams(() => ({ detail: serializeEnum(value) })),
      // Keep the range valid: a start after the end pulls the end to now, an end before the start pulls the start back.
      // Both dates are patched together, from the latest URL, so they always change in the same navigation.
      setFilterDateStart: (value) => {
        if (!isSelectableDate(value)) return;

        updateFilterParams((current) => {
          const { dateEnd: currentEnd } = readDateRange(current.get('from'), current.get('to'));
          return {
            from: serializeDate(value),
            to: serializeDate(value && currentEnd && value.isAfter(currentEnd) ? dayjs() : currentEnd)
          };
        });
      },
      setFilterDateEnd: (value) => {
        if (!isSelectableDate(value)) return;

        updateFilterParams((current) => {
          const { dateStart: currentStart } = readDateRange(current.get('from'), current.get('to'));
          return {
            from: serializeDate(
              value && currentStart && value.isBefore(currentStart) ? value.subtract(1, 'hour') : currentStart
            ),
            to: serializeDate(value)
          };
        });
      },
      clearFilterDates: () => updateFilterParams(() => ({ from: undefined, to: undefined })),
      setFilterUser: (value) => updateFilterParams(() => ({ user: value ?? undefined })),
      setFilterDevice: (value) => updateFilterParams(() => ({ device: value ?? undefined }))
    },
    clearAllFilters,
    isAnyFilterEnabled
  };

  const sections = createEventFilterSections({ translation, filters, visitorOptions, deviceOptions });
  const chips = createEventFilterChips({ translation, filters, visitorOptions, deviceOptions });

  return { sections, chips, apiFilter, isAnyFilterEnabled, clearAllFilters };
};
